/**
 * Add ONE person to groups — the replacement for "clonar desde otra
 * persona".
 *
 * The affordance the retired page had was `cloneFrom`, which copied a
 * snapshot of somebody's rows. This is the same intent expressed as
 * membership, and it is strictly better by Pablo's own argument for
 * one-person groups (2026-09-07): a group survives the person, so "same
 * access as Ana" keeps working after Ana leaves, while a cloned row set
 * silently stops matching the role it was copied from.
 *
 * Adds one at a time (`POST /{group}/members/{user}`), never the bulk
 * PUT: that endpoint REPLACES a group's membership with the list it is
 * given, so a dialog whose job is to add must not send one.
 */

import { Button, Modal } from "@omnitok/ui";
import { Callout, EmptyState } from "@pervasivemind/admin-ui";
import { Check, Plus, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import { SCOPE_ALL, groupsApi, type GroupSummaryOut } from "@/lib/access-api";
import { addInOrder } from "@/pages/admin-access/AddMembersModal";

export function JoinGroupsModal({
  open,
  userId,
  allGroups,
  current,
  onClose,
  onJoined,
  onChanged,
}: {
  open: boolean;
  userId: string;
  allGroups: readonly GroupSummaryOut[];
  current: readonly string[];
  onClose: () => void;
  onJoined: (note: string) => void;
  /** Some were joined before a refusal: refresh, keep the modal open. */
  onChanged: (note: string) => void;
}): JSX.Element | null {
  const { t } = useT();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set());
    setError(null);
  }, [open]);

  const offerable = useMemo(
    () => allGroups.filter((g) => !current.includes(g.id)),
    [allGroups, current],
  );

  if (!open) return null;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const out = await addInOrder([...picked], (id) => groupsApi.addMember(id, userId));
      if (out.error === null) {
        onJoined(out.note);
        return;
      }
      // Partial application is reported, not hidden: whoever went
      // through already has access, so the caller refreshes NOW and the
      // picker stops offering them.
      setError(out.error);
      if (out.added.length > 0) {
        setPicked((prev) => {
          const next = new Set(prev);
          for (const id of out.added) next.delete(id);
          return next;
        });
        onChanged(out.note);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      title={t("access.people.groups.joinTitle")}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button ghost size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={picked.size === 0 || busy}
            onClick={() => void submit()}
          >
            {t("access.people.groups.joinCount", { count: picked.size })}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error !== null && (
          <Callout tone="danger" title={t("access.common.errorTitle")}>
            <span className="whitespace-pre-wrap">{error}</span>
          </Callout>
        )}

        {offerable.length === 0 ? (
          <EmptyState
            icon={<Users size={20} />}
            title={t("access.people.groups.noneOfferable")}
            description={t("access.people.groups.noneOfferableDesc")}
          />
        ) : (
          <ul className="max-h-80 space-y-1 overflow-y-auto">
            {offerable.map((g) => {
              const on = picked.has(g.id);
              return (
                <li key={g.id}>
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (next.has(g.id)) next.delete(g.id);
                        else next.add(g.id);
                        return next;
                      })
                    }
                    className={
                      on
                        ? "flex w-full items-center gap-2 rounded-md border border-primary-light bg-primary-soft px-3 py-1.5 text-left text-sm font-semibold text-primary-ink"
                        : "flex w-full items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    }
                  >
                    {on ? (
                      <Check size={14} strokeWidth={3} aria-hidden="true" />
                    ) : (
                      <Plus size={14} strokeWidth={3} aria-hidden="true" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{g.name}</span>
                    <span className="truncate font-mono text-[13px] text-neutral-500">
                      {g.scope_id === SCOPE_ALL
                        ? t("access.scopes.all")
                        : g.scope_id}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[13px] text-neutral-600">
          {t("access.people.groups.joinNote")}
        </p>
      </div>
    </Modal>
  );
}
